import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

// ── Types ──────────────────────────────────────────────────────────────────

interface ConversionSummary {
  totalRevenue: number;
  attributedRevenue: number;
  attributionRate: number;
  totalConversions: number;
  attributedConversions: number;
  avgOrderValue: number;
  revenueChange: number | null;
  attributedRevenueChange: number | null;
}

interface TimelineRow {
  date: string;
  revenue: number;
  attributedRevenue: number;
  conversions: number;
  attributedConversions: number;
}

interface GoalRow {
  goalId: number;
  goalName: string;
  triggerType: string;
  conversions: number;
  attributedConversions: number;
  revenue: number;
  attributedRevenue: number;
  attributionRate: number;
}

interface ReportData {
  summary: ConversionSummary;
  timeline: TimelineRow[];
  byGoal: GoalRow[];
  goalCount: number;
  hasData: boolean;
}

// ── Integration upsell catalog ─────────────────────────────────────────────

const INTEGRATION_UPSELLS = [
  {
    key: "shopify",
    name: "Shopify",
    icon: "fab fa-shopify",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
    description: "Auto-track every order completed after a chat. No code needed.",
    setupPath: "/integrations/shopify"
  },
  {
    key: "square",
    name: "Square",
    icon: "fas fa-square",
    color: "bg-blue-50 text-blue-700 border-blue-200",
    description: "Link Square payments to chat sessions automatically.",
    setupPath: "/integrations/square-appointments"
  },
  {
    key: "bigcommerce",
    name: "BigCommerce",
    icon: "fas fa-shopping-bag",
    color: "bg-indigo-50 text-indigo-700 border-indigo-200",
    description: "Pull BigCommerce order revenue into attribution reports.",
    setupPath: "/integrations/bigcommerce"
  },
  {
    key: "woocommerce",
    name: "WooCommerce",
    icon: "fas fa-wordpress",
    color: "bg-purple-50 text-purple-700 border-purple-200",
    description: "Track WooCommerce purchases attributed to live chat.",
    setupPath: "/integrations/woocommerce"
  },
  {
    key: "gtm",
    name: "Google Tag Manager",
    icon: "fab fa-google",
    color: "bg-orange-50 text-orange-700 border-orange-200",
    description: "Push conversion events to GTM dataLayer for GA4 and ads.",
    setupPath: "/integrations/google-tag-manager"
  }
];

// ── Helpers ────────────────────────────────────────────────────────────────

const fmt$ = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const fmtPct = (n: number) => `${(n * 100).toFixed(1)}%`;
const fmtNum = (n: number) => new Intl.NumberFormat("en-US").format(n);

function TrendBadge({ value }: { value: number | null }) {
  if (value == null) return null;
  const good = value > 0;
  const neutral = value === 0;
  const cls = neutral
    ? "bg-slate-100 text-slate-500"
    : good ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700";
  return (
    <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>
      {good ? "↑" : value < 0 ? "↓" : ""} {Math.abs(value)}% vs prior
    </span>
  );
}

function KpiCard({
  label, value, sub, accent, icon, trend
}: {
  label: string; value: string; sub?: string; accent: string; icon: string; trend?: number | null;
}) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 flex flex-col gap-2 shadow-sm">
      <div className="flex items-center gap-2">
        <div className={`rounded-lg p-2 shrink-0 ${accent}`}>
          <i className={`${icon} text-sm`} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
      </div>
      <div className="text-3xl font-bold text-slate-800 dark:text-slate-100 leading-tight">{value}</div>
      {sub && <div className="text-sm text-slate-500 dark:text-slate-400">{sub}</div>}
      {trend != null && <TrendBadge value={trend} />}
    </div>
  );
}

const TRIGGER_LABELS: Record<string, string> = {
  url: "URL Visit",
  shopify_order: "Shopify Order",
  square_payment: "Square Payment",
  bc_order: "BigCommerce Order",
  woo_order: "WooCommerce Order",
  magento_order: "Magento Order",
  custom_event: "Custom Event"
};

// ── Empty state ────────────────────────────────────────────────────────────

function EmptyState({ goalCount }: { goalCount: number }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 bg-violet-100 dark:bg-violet-900/30 rounded-2xl flex items-center justify-center mb-4">
        <i className="fas fa-chart-line text-2xl text-violet-500" />
      </div>
      <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
        {goalCount === 0 ? "No conversion goals yet" : "No conversions recorded yet"}
      </h2>
      <p className="text-slate-500 dark:text-slate-400 max-w-md mb-6">
        {goalCount === 0
          ? "Create your first conversion goal to start tracking which chats lead to purchases, sign-ups, or any action that matters to your business."
          : "Your goals are set up. Conversion events will appear here as visitors complete them after chatting."}
      </p>
      {goalCount === 0 && (
        <Link
          to="/settings/conversions"
          className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-medium px-5 py-2.5 rounded-lg transition-colors"
        >
          <i className="fas fa-plus text-sm" />
          Create first goal
        </Link>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function ConversionReport() {
  const api = useApi();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snippetOpen, setSnippetOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await (await api.admin.get(`Conversions/report?days=${days}`)).json() as ReportData;
      setData(res);
    } catch {
      setError("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [api, days]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <i className="fas fa-spinner fa-spin text-2xl text-violet-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-red-500">{error}</p>
        <button onClick={load} className="text-sm text-violet-600 underline">Retry</button>
      </div>
    );
  }

  const { summary, timeline, byGoal, goalCount, hasData } = data!;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">

      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Conversion Attribution</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Track which conversations drive purchases and sign-ups
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Link
            to="/settings/conversions"
            className="flex items-center gap-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors"
          >
            <i className="fas fa-cog text-xs" />
            Manage Goals
          </Link>
        </div>
      </div>

      {!hasData && goalCount === 0 ? (
        <EmptyState goalCount={goalCount} />
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Attributed Revenue"
              value={fmt$(summary.attributedRevenue)}
              sub={`of ${fmt$(summary.totalRevenue)} total`}
              accent="bg-violet-100 dark:bg-violet-900/30 text-violet-600"
              icon="fas fa-dollar-sign"
              trend={summary.attributedRevenueChange}
            />
            <KpiCard
              label="Attribution Rate"
              value={fmtPct(summary.attributionRate)}
              sub={`${fmtNum(summary.attributedConversions)} of ${fmtNum(summary.totalConversions)} conversions`}
              accent="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600"
              icon="fas fa-link"
            />
            <KpiCard
              label="Total Conversions"
              value={fmtNum(summary.totalConversions)}
              accent="bg-blue-100 dark:bg-blue-900/30 text-blue-600"
              icon="fas fa-check-circle"
              trend={summary.revenueChange}
            />
            <KpiCard
              label="Avg Order Value"
              value={fmt$(summary.avgOrderValue)}
              sub="per conversion"
              accent="bg-amber-100 dark:bg-amber-900/30 text-amber-600"
              icon="fas fa-shopping-cart"
            />
          </div>

          {!hasData && goalCount > 0 && <EmptyState goalCount={goalCount} />}

          {hasData && (
            <>
              {/* Timeline chart */}
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">Revenue Attribution Over Time</h2>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={timeline} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#a78bfa" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradAttr" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#34d399" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                    <Tooltip
                      formatter={(val, name) => [fmt$(Number(val ?? 0)), name === "revenue" ? "Total Revenue" : "Chat Attributed"]}
                      labelFormatter={l => `Date: ${l}`}
                    />
                    <Legend formatter={v => v === "revenue" ? "Total Revenue" : "Chat Attributed"} />
                    <Area type="monotone" dataKey="revenue" stroke="#a78bfa" fill="url(#gradTotal)" strokeWidth={2} />
                    <Area type="monotone" dataKey="attributedRevenue" stroke="#34d399" fill="url(#gradAttr)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Goals breakdown */}
              {byGoal.length > 0 && (
                <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700">
                    <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Goals Breakdown</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-700/50">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Goal</th>
                          <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Type</th>
                          <th className="text-right px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Conversions</th>
                          <th className="text-right px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Revenue</th>
                          <th className="text-right px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Chat Attr.</th>
                          <th className="text-right px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Attr. Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {byGoal.map(g => (
                          <tr key={g.goalId} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                            <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{g.goalName}</td>
                            <td className="px-4 py-3">
                              <span className="inline-block text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full">
                                {TRIGGER_LABELS[g.triggerType] ?? g.triggerType}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300">{fmtNum(g.conversions)}</td>
                            <td className="px-4 py-3 text-right font-medium text-slate-800 dark:text-slate-100">{fmt$(g.revenue)}</td>
                            <td className="px-4 py-3 text-right text-emerald-600 font-medium">{fmt$(g.attributedRevenue)}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 bg-slate-100 dark:bg-slate-700 rounded-full h-1.5">
                                  <div
                                    className="bg-emerald-400 h-1.5 rounded-full"
                                    style={{ width: `${Math.min(g.attributionRate * 100, 100)}%` }}
                                  />
                                </div>
                                <span className="text-slate-600 dark:text-slate-300 w-10 text-right">{fmtPct(g.attributionRate)}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Integration opportunities */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Integration Opportunities</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Connect these integrations for native, zero-code conversion tracking
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {INTEGRATION_UPSELLS.map(intg => (
            <Link
              key={intg.key}
              to={intg.setupPath}
              className={`flex items-start gap-3 p-4 rounded-lg border hover:shadow-md transition-all ${intg.color}`}
            >
              <i className={`${intg.icon} text-lg mt-0.5`} />
              <div className="min-w-0">
                <div className="font-semibold text-sm">{intg.name}</div>
                <div className="text-xs opacity-80 mt-0.5 leading-snug">{intg.description}</div>
              </div>
              <i className="fas fa-arrow-right text-xs ml-auto mt-1 opacity-60 shrink-0" />
            </Link>
          ))}
        </div>
      </div>

      {/* JS tracking snippet */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm">
        <button
          onClick={() => setSnippetOpen(v => !v)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
        >
          <div className="flex items-center gap-2">
            <i className="fas fa-code text-violet-500 text-sm" />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Custom JS Tracking Snippet</span>
            <span className="text-xs text-slate-400 dark:text-slate-500">— fire from any page or button click</span>
          </div>
          <i className={`fas fa-chevron-${snippetOpen ? "up" : "down"} text-xs text-slate-400`} />
        </button>
        {snippetOpen && (
          <div className="border-t border-slate-100 dark:border-slate-700 p-5">
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
              Add this to your checkout confirmation page or any conversion point. The Velaro widget must be present on the page.
            </p>
            <pre className="bg-slate-900 text-emerald-400 text-xs rounded-lg p-4 overflow-x-auto leading-relaxed">
{`<!-- Place after your Velaro widget embed -->
<script>
  // Replace GOAL_ID with your conversion goal ID from Settings → Conversions
  // amount is optional — omit to use the goal's default amount
  if (window.velaro) {
    velaro('trackConversion', {
      goalId: GOAL_ID,
      amount: 99.00  // optional: actual order total
    });
  }
</script>`}
            </pre>
            <div className="mt-3 flex items-center gap-3">
              <Link
                to="/settings/conversions"
                className="text-sm text-violet-600 hover:text-violet-700 dark:text-violet-400 font-medium"
              >
                Get your goal IDs →
              </Link>
              <Link
                to="/integrations/google-tag-manager"
                className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400"
              >
                Or use Google Tag Manager
              </Link>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
